import { Routes, Route, useParams } from 'react-router-dom';
import { useMyServices, serviceProviderScope } from '@/api/services';
import { VMWARE_SCOPE } from '@/api/vmware/_shared';
import { ComputeServicePicker } from './ComputeServicePicker';
import { ComputeConsole } from './ComputeConsole';
import { VmwareConsole } from '../vmware/VmwareConsole';

/**
 * Compute module — shared URL space for OpenNebula (Techev Cloud) and VMware
 * (Enterprise Compute). The service picker lists both; the console root resolves
 * the service's provider scope and renders the matching console so each provider
 * keeps its own navigation and tabs without affecting the other.
 *
 *   /services/compute                 → service picker (OpenNebula + VMware)
 *   /services/compute/:serviceId/*    → ScopeRouter → ComputeConsole | VmwareConsole
 */
export function ComputePage() {
  return (
    <Routes>
      <Route index element={<ComputeServicePicker />} />
      <Route path=":serviceId/*" element={<ScopeRouter />} />
    </Routes>
  );
}

/**
 * Resolves the service's provider scope from the already-fetched service list
 * (no extra fetch) and renders the matching console. While the list is loading
 * nothing is rendered (the console shells have their own skeleton states once
 * mounted). OpenNebula path is byte-for-byte unchanged — only VMware branches.
 */
function ScopeRouter() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: services = [] } = useMyServices();

  const svc = services.find((s) => s.id === serviceId);
  // Scope is unknown until the service list loads. Render nothing rather than
  // flashing the wrong console — both consoles mount their own skeletons.
  if (services.length === 0) return null;

  // Unknown service (not yet in list) defaults to OpenNebula console.
  const scope = svc ? serviceProviderScope(svc) : '';
  if (scope === VMWARE_SCOPE) {
    return <VmwareConsole />;
  }
  return <ComputeConsole />;
}

export default ComputePage;
